import { useState } from "react";
import { message } from "antd";
import sumBy from "lodash/sumBy";
import db from "../../../../database";
import { getNewOmsOrder, updateOMSOrderStatus } from "../../../../services";
import messages from "../../../../errors/messages.en-IN.json";
import All from "../../../../assets/images/all.svg";
import ParkedOrder from "../../../../assets/images/parkedOrder.svg";
import InProgress from "../../../../assets/images/inProgress.svg";
import Pending from "../../../../assets/images/pending.svg";
import Prepared from "../../../../assets/images/prepared.svg";
import Completed from "../../../../assets/images/completed.svg";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { showPrintFailure } from "../../../../lib/printer/printFailureNotice";

/**
 * Provides OMS/barcode order status handlers and the orderStatus config array:
 * handleOrderStatus, handleOrderStatusSelection, nextOrderStatus, setStatus,
 * handleStatusButton, handleInProgress, handleAccept, handleBarcodeOrderPrint
 */
const useOrderStatusHandlers = ({ tillData }) => {
  const orderStatus = [
    { title: "All", imgSrc: All, statusValue: "NE" },
    { title: "Parked", imgSrc: ParkedOrder, statusValue: "NE" },
    { title: "Pending", imgSrc: Pending, statusValue: "NE" },
    { title: "In Progress", imgSrc: InProgress, statusValue: "UP" },
    { title: "Prepared", imgSrc: Prepared, statusValue: "PR" },
    { title: "Completed", imgSrc: Completed, statusValue: "DE" },
  ];

  const [displayOrderItemsModal, setDisplayOrderItemsModal] = useState(false);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState(orderStatus[0]);

  const orderDetailsList = useRestaurantStore.getState().orderDetails;
  const initialOrderList = orderDetailsList ? orderDetailsList : [];
  const [orderDetails, setOrderDetails] = useState(initialOrderList);

  const [barcodeOrderList, setBarcodeOrderList] = useState([]);
  const [barcodeOrderStatusModal, setBarcodeOrderStatusModal] = useState(false);

  const handleOrderStatus = async () => {
    db.orders
      .orderBy("orderTime")
      .limit(20)
      .reverse()
      .toArray()
      .then((data) => {
        if (data?.length > 0) {
          data?.map((item) => {
            let time = new Date(item.orderTime);
            let newTime = time.toLocaleString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            });
            let newLineItems = item?.items?.map((itemLine) => ({
              ...itemLine,
              price: itemLine?.salesPrice || 0,
              quantity: itemLine?.weight || 0,
            }));
            let orderObj = {
              cWCOrderId: item.sOrderID || "",
              customerId: item?.customer?.cwrCustomerId || "",
              customerName: item?.customer?.name || "",
              lineItems: [...newLineItems],
              mobileNo: item?.customer?.mobileNo,
              noOfItems: item?.items?.length,
              orderNo: item.documentno,
              posOrders: "Y",
              status: "NE",
              total: item?.total || 0,
              totalQty: item?.totalQty || 0,
              dateCreated: newTime,
            };
            let findIndexOrder = orderDetails.findIndex((item) => item.cWCOrderId === orderObj.cWCOrderId);
            if (findIndexOrder === -1) {
              orderDetails.push(orderObj);
            } else {
              orderDetails[findIndexOrder] = orderObj;
            }
            return null;
          });
        }
      });

    const paramsInput = {
      query: `query{
        getNewOmsOrders(bunitId:"${tillData.tillAccess.csBunit.csBunitId}"){
        cWCOrderId
        orderNo
        dateCreated
        customerId
        customerName
        mobileNo
        total
        noOfItems
        totalQty
        lineItems{
            productId
            name
            productCode
            quantity
            price
            subTotal
            addOnProducts{
                id
                name
                price
            }
        }
    }
    }`,
    };
    try {
      const response = await getNewOmsOrder(paramsInput);
      const { getNewOmsOrders } = response.data.data;
      getNewOmsOrders?.map((item, index) => {
        let obj = { ...item };
        obj.status = "NE";
        obj.noOfItems = item.lineItems.length;
        obj.posOrders = "N";
        let time = new Date(item.dateCreated);
        let newTime = time.toLocaleString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        });
        obj.dateCreated = newTime;
        let findIndex = orderDetails.findIndex((o) => o.cWCOrderId === item.cWCOrderId);
        if (findIndex === -1) {
          orderDetails.push(obj);
        } else {
          orderDetails[findIndex] = obj;
        }
        return null;
      });
      useRestaurantStore.getState().setOrderDetails([...orderDetails]);
      setOrderDetails([...orderDetails]);
    } catch (err) {
      console.debug(err);
    }
    setDisplayOrderItemsModal(true);
  };

  const handleOrderStatusSelection = (record) => {
    setSelectedOrderStatus(record);
  };

  const nextOrderStatus = () => {
    let newOrderStatus = [...orderStatus];
    let statusIndex = newOrderStatus.findIndex((item) => item.title === selectedOrderStatus.title);
    if (statusIndex <= newOrderStatus.length - 1) setSelectedOrderStatus(newOrderStatus[statusIndex + 1]);
  };

  const setStatus = (status) => {
    let newStatus = "";
    switch (status) {
      case "NE":
        newStatus = "UP";
        break;
      case "UP":
        newStatus = "PR";
        break;
      case "PR":
        newStatus = "DE";
        break;
      default:
        newStatus = "NE";
    }
    return newStatus;
  };

  const handleInProgress = async (record) => {
    const paramsInput = {
      query: `mutation{
        updateOMSOrderStatus(order:{
            cWCOrderId: "${record.cWCOrderId}"
            status: "${record.status}"
        })
        {
            status
            message
        }
    }`,
    };
    try {
      const response = await updateOMSOrderStatus(paramsInput);
      if (response?.data?.data?.updateOMSOrderStatus?.status === "200") {
        let newOrderDetails = [...orderDetails];
        let recordIndex = newOrderDetails.findIndex((o) => o.cWCOrderId === record.cWCOrderId);
        newOrderDetails[recordIndex].status = setStatus(record.status);
        useRestaurantStore.getState().setOrderDetails(orderDetails);
        setOrderDetails([...newOrderDetails]);
        nextOrderStatus();
      }
    } catch (err) {
      console.debug(err);
    }
  };

  const handleStatusButton = (record) => {
    if ((record.status === "NE" || record.status === "UP" || record.status === "PR") && record.posOrders === "N") {
      handleInProgress(record);
    } else {
      let newOrderDetails = [...orderDetails];
      let recordIndex = newOrderDetails.findIndex((o) => o.cWCOrderId === record.cWCOrderId);
      newOrderDetails[recordIndex].status = setStatus(record.status);
      useRestaurantStore.getState().setOrderDetails(orderDetails);
      setOrderDetails([...newOrderDetails]);
      nextOrderStatus();
    }
  };

  const handleAccept = async (record) => {
    const paramsInput = {
      query: `mutation{
        updateOMSOrderStatus(order:{
            cWCOrderId: "${record.cWCOrderId}"
            status: "DE"
        })
        {
            status
            message
        }
    }`,
    };
    try {
      const response = await updateOMSOrderStatus(paramsInput);
      if (response?.data?.data?.updateOMSOrderStatus?.status === "200") {
        message.success(`Order ${barcodeOrderList[0]?.orderNo} ${messages.errors.cart.completed_successfully}`);
        setBarcodeOrderStatusModal(false);
      }
    } catch (err) {
      console.debug(err);
    }
  };

  const handleBarcodeOrderPrint = (printedData, tillName) => {
    const printWindow = window.open("", "printWindow", "width=0,height=0");
    let printedDataArray = printedData?.lineItems?.map((item) => ({
      ...item,
      productName: item?.name,
      price: item?.price,
      qty: item?.quantity,
    }));
    const barcodeOrderData = `
      <html>
      <style>
      th,td {
        padding: 15px;
      }
      </style>
        <div style="width:100%;">
        <div style="text-align:center; margin-bottom: 20px;"><h4>${tillName}</h4></div>
        <div style="display:flex; margin-bottom: 20px;">
        <span>Order No</span>
          <span style="margin-left: 100px;">${printedData.orderNo}</span>
        </div>
        <div style="display:flex; margin-bottom: 20px;">
        <span>Date</span>
          <span style="margin-left: 100px;">${printedData.dateCreated}</span>
        </div>
        <hr />
        <table style="width:100%">
              <tr>
                <th style="text-align:left;"><strong>Product</strong></th>
                <th style="text-align:left;"><strong>Qty</strong></th>
                <th style="text-align:left;"><strong>Price</strong></th>
              </tr>
              ${printedDataArray?.map((item) => {
                return `<tr>
                  <td>${item.productName}</td>
                  <td>${item.qty}</td>
                  <td>${item.price}</td>
                </tr>
                ${item?.addOnProducts?.map((addonItem) => {
                  return `<tr>
                    <td>--${addonItem.name}</td>
                    </tr>`;
                })}
               `;
              })}
          </table>
          <div style="display:flex; margin-top: 30px;">
          <h2>Total</h2>
            <h2 style="margin-left: 100px; text-align: end">${sumBy(printedDataArray, "price")}</h2>
          </div>
          <div style="text-align:center; margin-bottom: 20px;"><h4>Thank You</h4></div>
        </div>
      </html>`;
    // BUG-2570: a pop-up blocker returns null; say so instead of throwing.
    if (!printWindow) {
      showPrintFailure({ kind: "onlineOrder", ref: printedData?.orderNo, cause: "windowBlocked" });
      return;
    }
    printWindow.document.write(barcodeOrderData);
    printWindow.print();
    printWindow.close();
  };

  return {
    orderStatus,
    displayOrderItemsModal,
    setDisplayOrderItemsModal,
    selectedOrderStatus,
    setSelectedOrderStatus,
    orderDetails,
    setOrderDetails,
    barcodeOrderList,
    setBarcodeOrderList,
    barcodeOrderStatusModal,
    setBarcodeOrderStatusModal,
    handleOrderStatus,
    handleOrderStatusSelection,
    nextOrderStatus,
    setStatus,
    handleStatusButton,
    handleInProgress,
    handleAccept,
    handleBarcodeOrderPrint,
  };
};

export default useOrderStatusHandlers;
